import type { LucideIcon } from 'lucide-react';
import { Compass, Cpu, Play, Wrench } from 'lucide-react';
import type { AgentStarterPrompt, AgentStarterPromptIcon } from '@/lib/agent/starterPrompts';
import { DEFAULT_STARTERS } from '@/lib/agent/starterPrompts';
import { cn } from '@/lib/utils/cn';
import { AntiochAgentIcon } from './AntiochAgentIcon';

const STARTER_ICONS: Record<AgentStarterPromptIcon, LucideIcon> = {
  compass: Compass,
  play: Play,
  wrench: Wrench,
  cpu: Cpu,
};

export interface AgentChatEmptyStateProps {
  /** Click handler for starter prompt chips. When omitted, chips render as static suggestions. */
  onPickStarter?: (starter: AgentStarterPrompt) => void;
  /** Override the default starter list (e.g. for stories or context-specific suggestions). */
  starters?: AgentStarterPrompt[];
  className?: string;
}

/**
 * First-run state for an empty agent session.
 *
 * Anchored on the refreshed Antioch Agent mark so the chat reads as
 * a coherent surface from cold start through working.
 *
 * Below the headline we surface a short column of starter prompts.
 * Clicking one sends the full rich prompt immediately, while the
 * transcript renders the compact card metadata attached to that turn.
 */
export function AgentChatEmptyState({
  onPickStarter,
  starters = DEFAULT_STARTERS,
  className,
}: AgentChatEmptyStateProps) {
  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center px-6 py-10 text-center', className)}>
      <div className="relative mb-5 flex size-20 items-center justify-center">
        <div aria-hidden className="absolute inset-1 rounded-full bg-blue-500/10 blur-2xl dark:bg-blue-400/12" />
        <AntiochAgentIcon className="relative size-16 text-blue-500/25 dark:text-blue-300/25" />
        <AntiochAgentIcon className="absolute size-16 animate-shimmer-pulse text-blue-500 dark:text-blue-300" />
      </div>
      <p className="font-semibold text-[17px] text-gray-800 tracking-[-0.01em] dark:text-gray-100">
        Where do you want to start?
      </p>
      <p className="mt-1.5 max-w-md text-[13px] text-gray-500 leading-relaxed dark:text-gray-400">
        Explore, run, onboard, or upgrade. Pick a starting point or just type.
      </p>
      {starters.length > 0 && (
        <ul className="mt-7 flex w-full max-w-md flex-col gap-1.5 text-left">
          {starters.map((starter) => (
            <li key={starter.id}>
              <StarterButton starter={starter} onPick={onPickStarter} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface StarterButtonProps {
  starter: AgentStarterPrompt;
  onPick?: (starter: AgentStarterPrompt) => void;
}

function StarterButton({ starter, onPick }: StarterButtonProps) {
  const Icon = STARTER_ICONS[starter.iconKey];
  const interactive = Boolean(onPick);
  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={onPick ? () => onPick(starter) : undefined}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg border bg-white/70 px-3.5 py-2.5 text-left transition-[border-color,background-color,box-shadow] duration-150',
        'border-gray-200/80 dark:border-white/[0.08] dark:bg-white/[0.03]',
        interactive
          ? 'cursor-pointer hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-[0_10px_24px_-22px_rgba(15,23,42,0.35)] dark:hover:border-white/15 dark:hover:bg-white/[0.06]'
          : 'cursor-default'
      )}
    >
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-500 ring-1 ring-blue-100/80 transition-colors duration-150',
          'dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-300/15',
          interactive &&
            'group-hover:bg-blue-100/70 group-hover:text-blue-600 dark:group-hover:bg-blue-500/15 dark:group-hover:text-blue-200'
        )}
      >
        <Icon className="size-3.5" strokeWidth={1.75} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium text-[12.5px] text-gray-700 dark:text-gray-200">{starter.label}</span>
        <span className="truncate text-[11.5px] text-gray-500 dark:text-gray-400">{starter.description}</span>
      </span>
    </button>
  );
}
